import { PickersDayProps } from '@mui/x-date-pickers';
import { DesktopDatePicker as MuiDesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ReactComponent as ArrowDownIcon } from 'assett/icons/arrow_down_icon.svg';
import { ReactComponent as ArrowleftIcon } from 'assett/icons/arrow_left_datepicker_icon.svg';
import { ReactComponent as ArrowRightIcon } from 'assett/icons/arrow_right_datepicker_icon.svg';
import { ReactComponent as ArrowUpIcon } from 'assett/icons/arrow_up_icon.svg';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import Stack from 'styles/stack/Stack';
import { colors } from 'styles/theme/Palette';
import Typography from 'styles/typography/Typograpy';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import CustomDays from './CustomDays.component';

interface IRangePicker {
	name: string;
	dateStart: Dayjs | null;
	dateEnd: Dayjs | null;
	error?: boolean;
	errorMessage?: string;
	setStartDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
	setEndDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
	onClose?: () => void;
}

const PAPER_WIDTH = '270px';

const paperSx = {
	width: `${PAPER_WIDTH}!important`,
	'& .MuiPickersLayout-root': { width: `${PAPER_WIDTH}!important` },
	'& .MuiDateCalendar-root': { width: `${PAPER_WIDTH}!important` },
	'& .MuiPickersCalendarHeader-root': { width: '100%' },
	'& .MuiDayCalendar-header': { width: '238px', gap: 0 },
	'& .MuiDayCalendar-weekContainer': {
		width: '238px',
		height: 'auto',
		margin: '0px !important',
		padding: '2px 0',
	},
	'& .MuiPickersDay-root': { height: '34px', width: '34px', margin: 0 },
	'& .MuiDayCalendar-slideTransition': { height: '280px' },
};

const RangePicker = ({
	name,
	dateStart,
	dateEnd,
	error,
	errorMessage,
	setStartDate,
	setEndDate,
	onClose,
}: IRangePicker) => {
	const [open, setOpen] = useState(false);
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});

	const shrinkLabel = open || (!!dateStart && !!dateEnd) || error;

	const handleOpenPicker = () => setOpen(true);

	const handleClosePicker = () => {
		setOpen(false);
		onClose?.();
	};

	const onSelectDate = (e: Dayjs | null) => {
		if (!e) return;
		const selected = dayjs(e.format('YYYY-MM-DD'));

		// Both dates set or neither: start fresh selection
		if ((dateStart && dateEnd) || (!dateStart && !dateEnd)) {
			setStartDate(selected);
			setEndDate(null);
			return;
		}

		// Only start date set: determine end
		if (dateStart && !dateEnd) {
			if (selected.isSame(dateStart)) {
				setStartDate(null);
			} else if (selected.isAfter(dateStart)) {
				setEndDate(selected);
			} else {
				setEndDate(dateStart);
				setStartDate(selected);
			}
		}
	};

	const renderInputValue = () => {
		if (dateStart && dateEnd)
			return `${dateStart.format('DD/MM/YYYY')} - ${dateEnd.format('DD/MM/YYYY')}`;
		if (dateStart) return dateStart.format('DD/MM/YYYY');
		return '';
	};

	const CustomDay = useCallback(
		(pickersDayProps: PickersDayProps<Dayjs>) => {
			const { day } = pickersDayProps;
			const isInRange =
				dateStart &&
				dateEnd &&
				day.isAfter(dateStart, 'day') &&
				day.isBefore(dateEnd, 'day');
			const isStart = dateStart && day.isSame(dateStart, 'day');
			const isEnd = dateEnd && day.isSame(dateEnd, 'day');

			return (
				<CustomDays
					key={day.toString()}
					pickersDayProps={pickersDayProps}
					isInRange={isInRange}
					isStart={isStart}
					isEnd={isEnd}
					dateEnd={dateEnd}
				/>
			);
		},
		[dateStart, dateEnd],
	);

	return (
		<Stack position="relative">
			<DesktopDatePicker
				open={open}
				onOpen={handleOpenPicker}
				onClose={handleClosePicker}
				label={tClientLabels(`commons.datePickerHeader.${name}`)}
				onChange={onSelectDate as any}
				value={null}
				showDaysOutsideCurrentMonth
				closeOnSelect={false}
				slots={{
					day: CustomDay as any,
					openPickerIcon: () =>
						open ? <ArrowUpIcon /> : <ArrowDownIcon onClick={handleOpenPicker} />,
					leftArrowIcon: () => <ArrowleftIcon />,
					rightArrowIcon: () => <ArrowRightIcon />,
				}}
				slotProps={{
					popper: { sx: { width: PAPER_WIDTH, background: 'transparent' } },
					desktopPaper: {
						elevation: 0,
						style: { transform: 'translate(0px, 5px)' },
						sx: paperSx,
					},
					textField: {
						onClick: handleOpenPicker,
						fullWidth: true,
						variant: 'filled' as const,
						InputLabelProps: {
							shrink: shrinkLabel,
							sx: {
								color: error ? `${colors.error}!important` : '',
								'&.Mui-focused': { color: error ? `${colors.error}!important` : '' },
							},
						},
						inputProps: {
							sx: { cursor: 'pointer' },
							value: renderInputValue(),
							placeholder: 'dd/mm/yyyy - dd/mm/yyyy',
							readOnly: true,
						},
						InputProps: {
							sx: { borderColor: error ? `${colors.error}!important` : '' },
						},
					},
				}}
			/>
			{error && errorMessage && (
				<Typography
					variant="error"
					sx={{ position: 'absolute', bottom: '-22px', left: '0' }}
				>
					{tClientLabels(errorMessage)}
				</Typography>
			)}
		</Stack>
	);
};

const DesktopDatePicker = styled(MuiDesktopDatePicker)`
	width: 100%;
`;

export default RangePicker;
